import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { IconEye, IconEyeOff } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import CustomImage from '@/components/CustomImage';
import Input from '@/components/Input/Input';
import { getFCMToken } from '@/firebase/fcm';
import { useFormatText } from '@/hooks/useFormatText';
import { useAuthRole } from '@/layout/authLayout/authContext';
import AuthRoleSelector from '@/layout/authLayout/AuthRoleSelector';
import { TLoginForm, loginFormSchema } from '@/schema/loginSchema';
import { LoginApi, AppleLoginApi, GoogleLoginApi } from '@/service/authService';
import { connectWebSocket } from '@/service/websocket';
import { useParentStore } from '@/store/useParentStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import useUser from '@/store/useUser';
import { IUser } from '@/types';
import { getAssetUrl, generateOrGetUserId } from '@/utils';
import { encryptPassword } from '@/utils/encryption';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleLogin } from '@react-oauth/google';

interface ILoginResponse {
  status: boolean;
  message: string;
  data: {
    token: string;
    user: {
      name: string;
      email: string;
      user_role: string;
      profile_pic?: string;
      default_child?: unknown;
      settings?: {
        language?: string;
        date_format?: string;
        currency?: string;
      };
    };
  };
}

interface IAppleAuthResponse {
  authorization: {
    id_token: string;
    code: string;
    state?: string;
  };
  user?: {
    email: string;
    name: {
      firstName: string;
      lastName: string;
    };
  };
}

/* ─── component ─────────────────────────────────────────────────────── */
const Login = () => {
  const navigate = useNavigate();
  const setUser = useUser((state) => state.setUser);
  const setSelectedChild = useParentStore((state) => state.setSelectedChild);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const { formatText } = useFormatText();
  const { updateSettings } = useSettingsStore();
  const { role } = useAuthRole();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TLoginForm>({
    resolver: zodResolver(loginFormSchema),
    mode: 'onChange',
  });

  const watchedFields = watch(['email', 'password']);
  const [email, password] = watchedFields;

  /* ── helpers shared between normal & google login ── */
  const applyLoginResponse = (response: ILoginResponse) => {
    enqueueSnackbar(response?.message, { variant: 'success' });
    const user = response?.data?.user;
    setUser({
      token: response?.data?.token,
      name: user?.name,
      email: user?.email,
      is_access: user?.user_role as never,
      profile_pic: getAssetUrl(user?.profile_pic),
    });
    const loginDefaultChild = user?.default_child;
    if (loginDefaultChild) {
      setSelectedChild(loginDefaultChild as never);
    }
    const settings = user?.settings;
    updateSettings({
      language: settings?.language as never,
      direction: settings?.language === 'ar' ? 'rtl' : 'ltr',
      dateFormat: settings?.date_format as never,
      currency: settings?.currency as never,
    });
    navigate('/');
  };

  /* ── normal email / password login ── */
  const onFinish = async () => {
    if (loading) return;
    const encryptedPassword = encryptPassword(password);
    setLoading(true);
    try {
      let fcmToken: string | undefined;
      try {
        fcmToken = await getFCMToken();
      } catch (err) {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        console.warn('FCM token not available:', err);
      }
      const data: IUser = {
        email: email,
        password: encryptedPassword,
        device_id: generateOrGetUserId(),
        fcm_token: fcmToken,
        role,
      };
      const response = await LoginApi(data);
      if (response?.status) {
        applyLoginResponse(response as ILoginResponse);
        connectWebSocket();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /* ── Google OAuth login ── */
  const handleGoogleSuccess = async (idToken: string) => {
    setGoogleLoading(true);
    let fcmToken: string | undefined;
    try {
      fcmToken = await getFCMToken();
    } catch (err) {
      console.warn('FCM token not available:', err);
    }
    try {
      const response = await GoogleLoginApi({
        id_token: idToken,
        device_id: generateOrGetUserId(),
        fcm_token: fcmToken,
      });
      if (response?.status) {
        applyLoginResponse(response as ILoginResponse);
      }
    } catch (error) {
      console.error('Google login error:', error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSuccess = async (response: IAppleAuthResponse) => {
    setAppleLoading(true);
    try {
      let fcmToken: string | undefined;
      try {
        fcmToken = await getFCMToken();
      } catch (err) {
        console.warn('FCM token not available:', err);
      }
      const res = await AppleLoginApi({
        id_token: response.authorization.id_token,
        device_id: generateOrGetUserId(),
        fcm_token: fcmToken,
      });
      if (res?.status) {
        applyLoginResponse(res as ILoginResponse);
      }
    } catch (error) {
      console.error('Apple login error:', error);
    } finally {
      setAppleLoading(false);
    }
  };

  return (
    <div className="flex flex-col justify-center p-15 md:p-10 w-full lg:max-w-150">
      <div className="w-full flex flex-col items-center">
        {/* ── heading ── */}
        <div className="text-center mb-8">
          <p className="pb-2 text-4xl max-md:text-3xl font-bold text-neutral-primary tracking-[-0.36px] leading-11">
            {formatText('login.title')}
          </p>
          <p className="text-neutral-secondary text-lg leading-7">
            {formatText('login.sub-title')}
          </p>
        </div>
        {/* ── role selector ── */}
        <div className="w-full mb-6">
          <AuthRoleSelector />
        </div>
        {/* ── email / password form ── */}
        <form
          method="POST"
          className="w-full"
          onSubmit={handleSubmit(onFinish)}
        >
          <div className="flex flex-col gap-4">
            <Input
              label={formatText('login.form.email')}
              placeholder={formatText('login.form.email-placeholder')}
              name="email"
              type="email"
              required
              register={register}
              error={
                errors.email?.message
                  ? formatText(errors.email.message)
                  : undefined
              }
            />

            <Input
              label={formatText('login.form.password')}
              name="password"
              type={show ? 'text' : 'password'}
              placeholder={formatText('login.form.password-placeholder')}
              register={register}
              required
              rightIcon={{
                icon: show ? (
                  <IconEyeOff
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ) : (
                  <IconEye
                    size={20}
                    color="#99A0AE"
                    className="cursor-pointer"
                  />
                ),
                onClick: () => setShow((prev) => !prev),
              }}
              error={
                errors.password?.message
                  ? formatText(errors.password.message)
                  : undefined
              }
              rightIconBg={'#ffffff'}
            />
          </div>

          <div className="flex w-full justify-end items-center mt-4 mb-6">
            <Button
              variant="link"
              href="/forgot-password"
              label={formatText('login.form.actions.forgot-password')}
              className="text-neutral-primary"
            >
              {formatText('login.form.actions.forgot-password')}
            </Button>
          </div>

          <Button
            label={formatText('login.form.actions.login')}
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full py-3.5! leading-5"
          />
        </form>
        {/* ── divider ── */}
        <div className="flex items-center w-full my-6 gap-3">
          <div className="flex-1 h-px bg-neutral-200" />
          <span className="text-sm text-neutral-400 font-medium px-1">
            {formatText('login.or')}
          </span>
          <div className="flex-1 h-px bg-neutral-200" />
        </div>
        {/* ── social buttons ── */}
        <div className="flex gap-3 w-full max-md:flex-col">
          {/* Google */}
          <div className="relative flex flex-1">
            <Button
              id="google-login-btn"
              type="button"
              variant="outline"
              disabled={googleLoading}
              loading={googleLoading}
              icon={
                <CustomImage
                  src="/assets/svg/google.svg"
                  alt="Google"
                  width={20}
                  height={20}
                />
              }
              label={formatText('login.google-login')}
              className="flex w-full items-center justify-center gap-2.5 px-4 py-3 rounded-xl border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
            />

            <GoogleLogin
              onSuccess={(credentialResponse) => {
                if (credentialResponse.credential) {
                  handleGoogleSuccess(credentialResponse.credential);
                }
              }}
              onError={() => {
                enqueueSnackbar(formatText('login.google-login-failed'), {
                  variant: 'error',
                });
              }}
              containerProps={{
                style: {
                  position: 'absolute',
                  inset: 0,
                  opacity: 0,
                  cursor: 'pointer',
                },
              }}
            />
          </div>

          {/* Apple */}
          <div className="relative flex flex-1">
            <AppleSignin
              authOptions={{
                clientId: import.meta.env.VITE_APP_APPLE_CLIENT_ID || '',
                scope: 'email name',
                redirectURI:
                  import.meta.env.VITE_APP_APPLE_CALLBACK_URL ||
                  window.location.origin + '/',
                state: 'origin:web', // Good for tracking request source
                usePopup: true,
              }}
              uiType="dark"
              onSuccess={(response: IAppleAuthResponse) =>
                handleAppleSuccess(response)
              }
              onError={() => {
                enqueueSnackbar(formatText('login.apple-login-failed'), {
                  variant: 'error',
                });
              }}
              render={(props: { onClick: () => void }) => (
                <Button
                  {...props}
                  id="apple-login-btn"
                  type="button"
                  variant="outline"
                  disabled={appleLoading}
                  loading={appleLoading}
                  icon={
                    <CustomImage
                      src="/assets/svg/apple.svg"
                      alt="Apple"
                      width={20}
                      height={20}
                    />
                  }
                  label={formatText('login.apple-login')}
                  className="flex w-full items-center justify-center gap-2.5 px-4 py-3 rounded-xl border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              )}
            />
          </div>
        </div>

        {/* ── terms & privacy ── */}
        <p className="mt-6 text-center text-xs text-neutral-secondary leading-5">
          {formatText('login.terms.prefix')}{' '}
          <Link
            to="#"
            className="text-xs text-neutral-primary underline underline-offset-2 hover:text-brand-default"
          >
            {formatText('login.terms.terms-of-use')}{' '}
          </Link>
          {formatText('login.terms.and')}{' '}
          <Link
            to="#"
            className="text-xs text-neutral-primary underline underline-offset-2 hover:text-brand-default"
          >
            {formatText('login.terms.privacy-policy')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
